/**
 * Choose a project by typing: the server searches the projects the person can see (`GET /projects/v1?q=`),
 * a page at a time, so a list of thousands is never loaded to fill a dropdown. `ProjectChips` shows the
 * ones already chosen by name (looked up by id, not loaded as a list).
 */

import { useEffect, useRef, useState } from 'react';

import { routes } from '../api/client.ts';
import { useNames } from '../names.ts';
import { Button } from './ui.tsx';
import { SearchBox } from './ui/SearchBox.tsx';

interface Found {
  id: string;
  name: string;
}

export function ProjectPicker({ onPick, exclude = [], placeholder = 'Search projects', label = 'Search projects', className }: { onPick: (p: Found) => void; exclude?: string[]; placeholder?: string; label?: string; className?: string }) {
  const [typed, setTyped] = useState('');
  const [rows, setRows] = useState<Found[]>([]);
  const [more, setMore] = useState<string>();
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);
  // Typing asks the server after a short pause; the newest answer wins.
  useEffect(() => {
    const mine = ++seq.current;
    const t = setTimeout(() => {
      setBusy(true);
      routes
        .projectsPage({ q: typed.trim(), limit: 8 })
        .then((a) => {
          if (seq.current !== mine) return;
          setRows(a.items);
          setMore(a.next);
        })
        .catch(() => seq.current === mine && setRows([]))
        .finally(() => seq.current === mine && setBusy(false));
    }, typed ? 300 : 0);
    return () => clearTimeout(t);
  }, [typed]);
  const showMore = () => {
    if (!more) return;
    const mine = ++seq.current;
    setBusy(true);
    routes
      .projectsPage({ q: typed.trim(), limit: 8, cursor: more })
      .then((a) => {
        if (seq.current !== mine) return;
        setRows((r) => [...r, ...a.items]);
        setMore(a.next);
      })
      .finally(() => seq.current === mine && setBusy(false));
  };
  const left = rows.filter((r) => !exclude.includes(r.id));
  return (
    <div className={`grid gap-2 ${className ?? ''}`}>
      <SearchBox label={label} value={typed} onChange={setTyped} placeholder={placeholder} className="w-full max-w-sm" />
      <ul className="grid max-h-56 gap-0.5 overflow-y-auto rounded-lg bg-surface-secondary p-1" aria-busy={busy}>
        {left.map((p) => (
          <li key={p.id}>
            <Button size="sm" variant="ghost" className="w-full justify-start" onPress={() => onPick(p)}>
              {p.name}
            </Button>
          </li>
        ))}
        {left.length === 0 ? <li className="px-3 py-2 text-xs text-muted">{busy ? 'Searching…' : typed ? `No project matches "${typed.trim()}".` : 'No project to add.'}</li> : null}
        {more ? (
          <li>
            <Button size="sm" variant="ghost" className="w-full justify-start text-muted" isPending={busy} onPress={showMore}>
              Show more
            </Button>
          </li>
        ) : null}
      </ul>
    </div>
  );
}

/** The chosen projects, by name; each can be taken off. An id that is not visible shows as the id. */
export function ProjectChips({ ids, onRemove }: { ids: string[]; onRemove: (id: string) => void }) {
  const names = useNames(ids);
  if (ids.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-1.5">
      {ids.map((id) => (
        <li key={id}>
          <Button size="sm" variant="secondary" aria-label={`Take ${names.projects.get(id) ?? id} off`} onPress={() => onRemove(id)}>
            {names.projects.get(id) ?? id}
            <span aria-hidden="true">×</span>
          </Button>
        </li>
      ))}
    </ul>
  );
}
